import { useCallback, useId, useMemo, useState, useTransition } from "react";
import { numberFormatter } from "@common/helpers/number";
import { pluralize } from "@common/helpers/string";
import { TaskStatus } from "@common/types/task";
import { TASK_STATUS_CONFIG } from "@components/tasks/TaskStatusSelect/taskStatus.constants";
import type {
    BoardDisplayChange,
    BoardViewState,
} from "@design-system/Board/Board.types";
import { BoardDisplayPopover } from "@design-system/Board/BoardDisplayPopover";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import type { BoardCardType } from "./boardPlaygroundSettings";
import { TASK_SORT_OPTIONS } from "./taskBoardSort";
import {
    TASK_CARD_FIELD_CONFIGS,
    TASK_COLUMN_BY_OPTIONS,
    TASK_GROUP_BY_OPTIONS,
} from "./taskBoardView";
import styles from "./BoardPlayground.module.scss";

const STATUS_ITEMS = Object.values(TaskStatus).map((status) => {
    const { Icon, tooltip } = TASK_STATUS_CONFIG[status];
    return { value: status, label: tooltip, leading: <Icon aria-hidden /> };
});

const renderStatusValue = (values: readonly TaskStatus[]) =>
    values.length === 1
        ? TASK_STATUS_CONFIG[values[0]].tooltip
        : `${values.length} statuses`;

const formatTaskCount = (visibleCount: number, totalCount: number) => {
    const noun = pluralize("task", totalCount);
    const total = numberFormatter.format(totalCount);
    return visibleCount === totalCount
        ? `${total} ${noun}`
        : `${numberFormatter.format(visibleCount)} of ${total} ${noun}`;
};

interface BoardPlaygroundToolbarProps {
    cardType: BoardCardType;
    onCardTypeChange: (cardType: BoardCardType) => void;
    search: string | undefined;
    onSearchChange: (search: string | undefined) => void;
    statuses: TaskStatus[];
    onStatusesChange: (statuses: TaskStatus[]) => void;
    visibleCount: number;
    totalCount: number;
    controlsId: string;
    isControlsOpen: boolean;
    onControlsToggle: () => void;
    viewState: BoardViewState;
    /** Called in a transition: the board may re-render in the background. */
    onViewStateChange: (change: BoardDisplayChange) => void;
    isManualOrder: boolean;
}

/**
 * The playground's toolbar: the Card type toggle, then for task cards the
 * title search, status filter, task count and Display popover, and the
 * Controls toggle. The popover shows a change at once, while the board it
 * rebuilds still renders.
 */
export const BoardPlaygroundToolbar = ({
    cardType,
    onCardTypeChange,
    search,
    onSearchChange,
    statuses,
    onStatusesChange,
    visibleCount,
    totalCount,
    controlsId,
    isControlsOpen,
    onControlsToggle,
    viewState,
    onViewStateChange,
    isManualOrder,
}: Readonly<BoardPlaygroundToolbarProps>) => {
    const statusLabelId = useId();
    const isTasks = cardType === "tasks";
    // The popover builds each change on the value it shows, so while one
    // renders it shows that one: a second change would otherwise undo it.
    const [isViewPending, startViewTransition] = useTransition();
    const [pendingViewState, setPendingViewState] =
        useState<BoardViewState | null>(null);
    const shownViewState =
        isViewPending && pendingViewState !== null
            ? pendingViewState
            : viewState;
    const changeViewState = useCallback(
        (change: BoardDisplayChange) => {
            setPendingViewState({ ...shownViewState, ...change });
            startViewTransition(() => onViewStateChange(change));
        },
        [shownViewState, onViewStateChange],
    );
    const cardFieldColumns = useMemo(
        () => shownViewState.cardFields.map((id) => ({ id, options: {} })),
        [shownViewState.cardFields],
    );

    return (
        <HStack
            gap={8}
            align="center"
            justify="between"
            className={styles.toolbar}
        >
            <HStack gap={8} align="center">
                <ToggleGroup.Root<BoardCardType>
                    aria-label="Card type"
                    size="sm"
                    value={cardType}
                    onValueChange={(nextCardType) => {
                        if (nextCardType) {
                            onCardTypeChange(nextCardType);
                        }
                    }}
                >
                    <ToggleGroup.Item value="tasks">Tasks</ToggleGroup.Item>
                    <ToggleGroup.Item value="work">Work</ToggleGroup.Item>
                </ToggleGroup.Root>
                {isTasks && (
                    <>
                        <SearchFilter
                            filter={search}
                            onChange={onSearchChange}
                            minLength={1}
                            placeholder="Search tasks…"
                            containerClassName={styles.search}
                        />
                        <span
                            id={statusLabelId}
                            className={styles.visuallyHidden}
                        >
                            Status
                        </span>
                        <Picker.Select
                            items={STATUS_ITEMS}
                            multiple
                            value={statuses}
                            onValueChange={onStatusesChange}
                            placeholder="All statuses"
                            renderValue={renderStatusValue}
                            trigger={
                                <Picker.Button
                                    variant="primary"
                                    size="md"
                                    aria-labelledby={statusLabelId}
                                />
                            }
                        />
                    </>
                )}
            </HStack>
            <HStack gap={12} align="center">
                {isTasks && (
                    <span className={styles.resultCount}>
                        {formatTaskCount(visibleCount, totalCount)}
                    </span>
                )}
                <Button
                    variant="primary"
                    size="sm"
                    aria-expanded={isControlsOpen}
                    aria-controls={controlsId}
                    onClick={onControlsToggle}
                >
                    Controls
                </Button>
                {isTasks && (
                    <BoardDisplayPopover
                        value={shownViewState}
                        onChange={changeViewState}
                        columnByOptions={TASK_COLUMN_BY_OPTIONS}
                        groupByOptions={TASK_GROUP_BY_OPTIONS}
                        sortOptions={TASK_SORT_OPTIONS}
                        sortDisabledReason={
                            isManualOrder
                                ? "Manual order is on in Controls"
                                : undefined
                        }
                        cardFields={{
                            columns: cardFieldColumns,
                            columnConfigs: TASK_CARD_FIELD_CONFIGS,
                            onColumnsChange: (columns) =>
                                changeViewState({
                                    cardFields: columns.map(({ id }) => id),
                                }),
                        }}
                    />
                )}
            </HStack>
        </HStack>
    );
};
